// Pure calculation engine for the /pricing revenue-engine build calculator
// (Task 2 of docs/superpowers/plans/2026-07-14-pricing-calculator.md).
//
// No I/O, no logging of config/answers, no side effects — this module is
// safe to run on the server (source of truth for pricing) and, via
// publicConfig(), safe to ship a sanitized copy of the config to the
// browser. Never trust a client-computed total: the server always
// re-runs evaluate() against the real config before quoting a price.
//
// Keys, not ids: sections/questions/options are addressed everywhere by
// their stable `.key` field. Payload assigns each array row an auto `id`
// (see payload-types.ts) that is regenerated on every write — this engine
// never reads or depends on those ids.

import type { Calculator } from '../../../payload-types'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** The Calculator global's shape, as generated by Payload (Task 1). */
export type CalculatorConfig = Calculator

type CalculatorSection = NonNullable<CalculatorConfig['sections']>[number]
type CalculatorQuestion = NonNullable<CalculatorSection['questions']>[number]
type CalculatorOption = NonNullable<CalculatorQuestion['options']>[number]

/**
 * Answers keyed by question `.key`. Single-choice questions answer with the
 * selected option's key; multi-choice with an array of selected option
 * keys; sliders with a number.
 */
export type CalculatorAnswers = Record<string, string | string[] | number>

export type EstimateItem = {
  sectionKey: string
  questionKey: string
  label: string
  minCents: number
  maxCents: number
  worthCents: number | null
  /**
   * The selected option's raw `breakdownLabel`, if one was explicitly set
   * (null otherwise — even though `label` above already falls back to
   * `option.label` when this is absent). Presentation layers (BreakdownStack,
   * the estimate email) use this to decide whether a zero-cost item is
   * worth showing at all: a zero-range item with no explicit breakdownLabel
   * is a "nothing extra to build here" answer (e.g. branding already fully
   * built out) and gets skipped rather than rendered as a bare "$0" row.
   */
  breakdownLabel: string | null
  /**
   * The selected option's `multiplierPct`, if it composes into the total's
   * multiplier (timeline-style options) — null for options that don't
   * carry one. Presentation layers use this to build the single "applied
   * multiplier" summary line (see `snapshot.appliedMultiplierPct`) rather
   * than trying to re-derive which item(s) contributed it.
   */
  multiplierPct: number | null
}

export type EvaluateResult = {
  items: EstimateItem[]
  totalMinCents: number
  totalMaxCents: number
  /**
   * Composed multiplier as a whole percent, clamped to 300. 100 = no
   * change. DISPLAY ONLY — totals are computed from the raw clamped
   * fraction, not from this rounded value.
   */
  appliedMultiplierPct: number
}

/** publicConfig() output — same shape, minus the two server-only secrets. */
export type PublicCalculatorOption = Omit<CalculatorOption, 'worthCents'>
export type PublicCalculatorQuestion = Omit<CalculatorQuestion, 'options'> & {
  options?: PublicCalculatorOption[] | null
}
export type PublicCalculatorSection = Omit<CalculatorSection, 'questions'> & {
  questions?: PublicCalculatorQuestion[] | null
}
export type PublicCalculatorConfig = Omit<CalculatorConfig, 'hotLeadThresholdCents' | 'sections'> & {
  sections?: PublicCalculatorSection[] | null
}

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

type FlatQuestion = {
  sectionKey: string
  question: CalculatorQuestion
}

/** Flattens config.sections into a single ordered list of questions. */
function flattenQuestions(config: CalculatorConfig): FlatQuestion[] {
  const out: FlatQuestion[] = []
  for (const section of config.sections ?? []) {
    if (!section?.key) continue
    for (const question of section.questions ?? []) {
      if (!question?.key) continue
      out.push({ sectionKey: section.key, question })
    }
  }
  return out
}

/** Safe integer coercion — treats null/undefined/NaN as 0. */
function cents(value: number | null | undefined): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : 0
}

function equalsAnyValues(question: CalculatorQuestion): Set<string> {
  const values = question.showWhen?.equalsAny ?? []
  return new Set(values.map((v) => v.value).filter((v): v is string => typeof v === 'string'))
}

/** True when `answer` (the trigger question's answer) satisfies `equalsAny`. */
function matchesTrigger(answer: unknown, equalsAny: Set<string>): boolean {
  if (equalsAny.size === 0) return false
  if (Array.isArray(answer)) return answer.some((v) => equalsAny.has(String(v)))
  if (answer === undefined || answer === null) return false
  return equalsAny.has(String(answer))
}

const MAX_VISIBILITY_ITERATIONS = 50

// ---------------------------------------------------------------------------
// visibleQuestions
// ---------------------------------------------------------------------------

/**
 * Resolves `showWhen` branch rules to the set of currently-visible question
 * keys, iteratively — a question whose trigger question is itself hidden
 * stays hidden too (nested branch chains), regardless of what `answers`
 * says about it. Monotone fixed-point: a question, once visible, never
 * becomes hidden again within a single resolution, so this always
 * converges in at most one pass per question — MAX_VISIBILITY_ITERATIONS
 * is a defensive bound against a pathological/cyclic config, not something
 * a well-formed config should ever hit (a self-referencing or circular
 * showWhen chain simply never resolves those questions to visible, and the
 * loop exits as soon as a pass makes no further progress).
 */
export function visibleQuestions(config: CalculatorConfig, answers: CalculatorAnswers): string[] {
  const flat = flattenQuestions(config)
  const visible = new Set<string>()

  for (const { question } of flat) {
    if (!question.showWhen?.questionKey) visible.add(question.key)
  }

  let changed = true
  let iterations = 0
  while (changed && iterations < MAX_VISIBILITY_ITERATIONS) {
    changed = false
    iterations++
    for (const { question } of flat) {
      if (visible.has(question.key)) continue
      const triggerKey = question.showWhen?.questionKey
      if (!triggerKey) continue
      if (!visible.has(triggerKey)) continue
      const triggerAnswer = answers[triggerKey]
      if (matchesTrigger(triggerAnswer, equalsAnyValues(question))) {
        visible.add(question.key)
        changed = true
      }
    }
  }

  return flat.map(({ question }) => question.key).filter((key) => visible.has(key))
}

// ---------------------------------------------------------------------------
// evaluate
// ---------------------------------------------------------------------------

function itemLabel(option: CalculatorOption): string {
  return option.breakdownLabel || option.label
}

/** Linear interpolation of a slider's single-option range by its value. */
function sliderCost(question: CalculatorQuestion, value: number): number {
  const option = (question.options ?? [])[0]
  if (!option) return 0
  const min = cents(question.sliderMin)
  const max = cents(question.sliderMax)
  const optionMin = cents(option.addMinCents)
  const optionMax = cents(option.addMaxCents)
  const t = max === min ? 0 : Math.min(1, Math.max(0, (value - min) / (max - min)))
  return optionMin + t * (optionMax - optionMin)
}

export function evaluate(config: CalculatorConfig, answers: CalculatorAnswers): EvaluateResult {
  const visible = new Set(visibleQuestions(config, answers))
  const items: EstimateItem[] = []
  const multiplierFractions: number[] = []

  for (const section of config.sections ?? []) {
    if (!section?.key) continue
    for (const question of section.questions ?? []) {
      if (!question?.key || !visible.has(question.key)) continue // tamper guard: hidden questions' answers are ignored
      const answer = answers[question.key]
      const options = question.options ?? []

      if (question.type === 'slider') {
        if (typeof answer !== 'number' || !Number.isFinite(answer)) continue
        const option = options[0]
        const raw = sliderCost(question, answer)
        const value = Math.round(raw)
        items.push({
          sectionKey: section.key,
          questionKey: question.key,
          label: option ? itemLabel(option) : question.prompt,
          minCents: value,
          maxCents: value,
          worthCents: null,
          breakdownLabel: option?.breakdownLabel ?? null,
          multiplierPct: null, // sliders never carry a multiplier
        })
        // Sliders don't carry a discrete "selected option" for multiplier
        // purposes — the interpolation IS the price, nothing to compose.
        continue
      }

      const selectedKeys: string[] =
        question.type === 'multi'
          ? Array.isArray(answer)
            ? answer.map(String)
            : typeof answer === 'string'
              ? [answer]
              : []
          : typeof answer === 'string'
            ? [answer]
            : []

      for (const key of selectedKeys) {
        const option = options.find((o) => o?.key === key)
        if (!option) continue // unknown option key — ignored
        const optionMultiplierPct =
          typeof option.multiplierPct === 'number' && Number.isFinite(option.multiplierPct)
            ? option.multiplierPct
            : null
        items.push({
          sectionKey: section.key,
          questionKey: question.key,
          label: itemLabel(option),
          minCents: Math.round(cents(option.addMinCents)),
          maxCents: Math.round(cents(option.addMaxCents)),
          worthCents: typeof option.worthCents === 'number' ? option.worthCents : null,
          breakdownLabel: option.breakdownLabel ?? null,
          multiplierPct: optionMultiplierPct,
        })
        if (optionMultiplierPct !== null) {
          multiplierFractions.push(optionMultiplierPct / 100)
        }
      }
    }
  }

  const composedFraction = multiplierFractions.reduce((acc, f) => acc * f, 1)
  const clampedFraction = Math.min(composedFraction, 3) // 300% cap
  // Money math uses the RAW clamped fraction — appliedMultiplierPct is
  // rounded to a whole percent for display only. Deriving totals from the
  // rounded percent would drift by real dollars on compositions that don't
  // land on a whole percent (e.g. 133% x 107% = 142.31%).
  const appliedMultiplierPct = Math.round(clampedFraction * 100)

  const baseMin = cents(config.baseMinCents)
  const baseMax = cents(config.baseMaxCents)
  const summedMin = items.reduce((acc, item) => acc + item.minCents, 0)
  const summedMax = items.reduce((acc, item) => acc + item.maxCents, 0)

  const totalMinCents = Math.round((baseMin + summedMin) * clampedFraction)
  const totalMaxCents = Math.round((baseMax + summedMax) * clampedFraction)

  return { items, totalMinCents, totalMaxCents, appliedMultiplierPct }
}

// ---------------------------------------------------------------------------
// publicConfig
// ---------------------------------------------------------------------------

/**
 * Strips the two server-only secrets (per-option `worthCents` and the
 * global `hotLeadThresholdCents`) and nothing else — prices, copy, and
 * branch rules are all public by design and pass through unchanged.
 */
export function publicConfig(config: CalculatorConfig): PublicCalculatorConfig {
  const { hotLeadThresholdCents: _hotLeadThresholdCents, sections, ...rest } = config

  const publicSections: PublicCalculatorSection[] | null | undefined = sections?.map((section) => {
    const { questions, ...sectionRest } = section
    const publicQuestions: PublicCalculatorQuestion[] | null | undefined = questions?.map((question) => {
      const { options, ...questionRest } = question
      const publicOptions: PublicCalculatorOption[] | null | undefined = options?.map((option) => {
        const { worthCents: _worthCents, ...optionRest } = option
        return optionRest
      })
      return { ...questionRest, options: publicOptions }
    })
    return { ...sectionRest, questions: publicQuestions }
  })

  return { ...rest, sections: publicSections }
}
